/**
 * OBS-1 / OBS-3 (lado cliente) · Captura de errores del navegador y envío.
 *
 * Un error que solo existe en la consola de un jugador no existe para nadie
 * más. Esto engancha `error` y `unhandledrejection` y los manda al backend, que
 * los registra como una línea JSON más (`POST /client-error`), así los fallos de
 * cliente y de servidor caen en el MISMO sitio y se pueden correlacionar.
 *
 * OBS-3 añade lo que faltaba para que un reporte sea accionable en un beta
 * cerrado, donde no se le puede pedir a nadie que reproduzca el bug:
 *
 *  - **Migas de pan**: qué estaba pasando justo antes (pantallas, estados de
 *    conexión, fases de sala). Un stack dice dónde reventó; las migas, por qué.
 *  - **Id de sesión**: separa "un jugador con un bug persistente" de "veinte
 *    jugadores con el mismo bug", que se ven igual si solo miras el total.
 *  - **Destino externo opcional** (`VITE_ERROR_ENDPOINT`): cualquier colector
 *    HTTP con plan gratuito. Y ahora funciona **aunque no haya servidor de
 *    juego configurado** — antes, un despliegue solo-front se quedaba sin
 *    ninguna telemetría de errores, que es justo cuando más falta hace.
 *
 * Restricciones que se respetan a propósito:
 *  - **Presupuesto**: tope por sesión y deduplicación por firma. Un error dentro
 *    del bucle de render dispara 60 por segundo; enviarlos todos sería un
 *    auto-DDoS y ni siquiera aportaría información nueva.
 *  - **Sin datos personales** (ver docs/PRIVACIDAD.md): id de invitado anónimo,
 *    nunca un nombre, y la URL va con los VALORES redactados.
 *  - **`text/plain`**: evita el preflight CORS. El cuerpo sigue siendo JSON.
 *  - **Nunca lanza**: un fallo al reportar un fallo no puede romper la partida.
 */
import { getGuestId } from "../lobby/identity";
import { regionHttpBase } from "../lobby/regions";
import {
  BreadcrumbTrail, Breadcrumb, CrumbKind, redactUrl, errorSignature, trimStack,
} from "./breadcrumbs";

/** Máximo de errores distintos reportados por sesión. */
const MAX_REPORTS = 10;
/** Tope de longitud del stack que se envía (el resto no aporta). */
const MAX_STACK = 1200;

const ENV = (import.meta as unknown as { env?: Record<string, string | undefined> }).env ?? {};
/** Modo de build (dev/production). */
const BUILD_MODE = ENV.MODE ?? "unknown";
/**
 * Identificador del build. Sin esto, un error solo dice "pasa en producción";
 * con esto dice en QUÉ versión, que es lo que permite saber si un despliegue
 * arregló algo o lo rompió.
 */
const BUILD_ID = ENV.VITE_BUILD_ID ?? BUILD_MODE;
/** Colector externo opcional (OBS-3, plan gratuito de cualquier proveedor). */
const EXTERNAL_ENDPOINT = ENV.VITE_ERROR_ENDPOINT;

let installed = false;
let sent = 0;
const seen = new Set<string>();

/**
 * Id de esta SESIÓN de juego (esta pestaña, esta vez).
 *
 * No es el de invitado, que identifica el dispositivo: sin distinguir sesiones,
 * "un jugador con un bug que se repite" y "veinte jugadores con el mismo bug"
 * producen el mismo número, y son problemas muy distintos.
 */
const SESSION_ID = Math.random().toString(36).slice(2, 10);

/** Migas de la sesión. Vive aquí para que cualquier módulo pueda dejar una. */
const trail = new BreadcrumbTrail();

/**
 * Deja una miga. Barato y sin efectos: se puede llamar desde cualquier sitio.
 *
 * El mensaje debe ser de vocabulario CERRADO (una etiqueta, no una frase, y
 * nunca texto que haya escrito una persona): esto acaba en un sistema de
 * errores, que es el lugar más fácil del mundo para filtrar datos sin querer.
 */
export function breadcrumb(kind: CrumbKind, msg: string): void {
  trail.add(kind, msg);
}

interface ErrorReport {
  kind: "error" | "unhandledrejection";
  message: string;
  stack: string;
  url: string;
  ua: string;
  guestId: string;
  build: string;
  sessionId: string;
  breadcrumbs: Breadcrumb[];
}

function postTo(url: string, body: string): void {
  try {
    // `sendBeacon` sobrevive a que la pestaña se esté cerrando, que es
    // justamente cuando llegan muchos errores. Si no está, `fetch` con
    // `keepalive` hace lo mismo peor.
    const beacon = navigator.sendBeacon?.bind(navigator);
    if (beacon && beacon(url, new Blob([body], { type: "text/plain" }))) return;
    void fetch(url, {
      method: "POST",
      body,
      headers: { "content-type": "text/plain" },
      keepalive: true,
    }).catch(() => { /* el reporte es best-effort: si no sale, no pasa nada */ });
  } catch {
    /* nunca romper la partida por un reporte */
  }
}

function post(report: ErrorReport, base: string | undefined): void {
  const body = JSON.stringify(report);
  if (base) postTo(`${base}/client-error`, body);
  // El colector externo es independiente: si el servidor de juego es justo lo
  // que está caído, es el único que va a recibir la señal.
  if (EXTERNAL_ENDPOINT) postTo(EXTERNAL_ENDPOINT, body);
}

function report(
  base: string | undefined,
  kind: ErrorReport["kind"],
  message: string,
  stack: string,
): void {
  if (sent >= MAX_REPORTS) return;
  const sig = errorSignature(kind, message, stack);
  if (seen.has(sig)) return;
  seen.add(sig);
  sent++;
  post({
    kind,
    message: message.slice(0, 300),
    stack: trimStack(stack, MAX_STACK),
    // Redactada: la URL lleva `?join=<sala>` y puede llevar un nombre.
    url: redactUrl(location.pathname, location.search),
    ua: navigator.userAgent.slice(0, 200),
    guestId: getGuestId(),
    build: BUILD_ID,
    sessionId: SESSION_ID,
    breadcrumbs: trail.snapshot(),
  }, base);
}

/**
 * Instala los capturadores. No-op si ya estaban instalados.
 *
 * A diferencia de antes, NO se rinde cuando no hay servidor de juego: con un
 * colector externo configurado sigue habiendo dónde mandar los errores, y un
 * despliegue solo-front es precisamente el que se queda sin ninguna otra señal.
 */
/**
 * Bloque técnico legible para un reporte que escribe UNA PERSONA.
 *
 * Es el mismo contexto que acompaña a un error automático, en texto plano y sin
 * nada que haya escrito nadie: build, sesión, navegador, pantalla y las últimas
 * migas. Lo que convierte "a veces se traba" en algo investigable.
 *
 * Va aparte del reporte automático a propósito: aquí la persona lo VE antes de
 * mandarlo. Un informe de diagnóstico que el usuario no puede leer es justo lo
 * que hace que la gente desconfíe de darle a "enviar".
 */
export function diagnosticsText(extra: Record<string, string> = {}): string {
  const nav = typeof navigator === "undefined" ? undefined : navigator;
  const scr = typeof window === "undefined" ? undefined : window.screen;
  const lines = [
    `build: ${BUILD_ID} (${BUILD_MODE})`,
    `sesión: ${SESSION_ID}`,
    `invitado: ${getGuestId()}`,
    `navegador: ${nav?.userAgent ?? "?"}`,
    `pantalla: ${scr ? `${scr.width}x${scr.height}` : "?"} · ventana: ${
      typeof window === "undefined" ? "?" : `${window.innerWidth}x${window.innerHeight}`}`
      + ` · dpr: ${typeof window === "undefined" ? "?" : window.devicePixelRatio}`,
  ];
  for (const [k, v] of Object.entries(extra)) lines.push(`${k}: ${v}`);
  const crumbs = trail.snapshot();
  lines.push("", `últimos pasos (${crumbs.length}):`);
  for (const c of crumbs) lines.push(`  ${(c.t / 1000).toFixed(1)}s ${c.kind} ${c.msg}`);
  return lines.join("\n");
}

export function installClientErrorReporting(): void {
  if (installed) return;
  const base = regionHttpBase();
  // Sin backend Y sin colector externo no hay dónde mandarlo; la consola del
  // navegador ya lo enseña y no tiene sentido montar los escuchas.
  if (!base && !EXTERNAL_ENDPOINT) return;
  installed = true;
  breadcrumb("nav", `session-start ${BUILD_ID}`);

  window.addEventListener("error", (e: ErrorEvent) => {
    const err = e.error as Error | undefined;
    report(base, "error", err?.message ?? e.message ?? "error", err?.stack ?? `${e.filename}:${e.lineno}:${e.colno}`);
  });

  window.addEventListener("unhandledrejection", (e: PromiseRejectionEvent) => {
    const r = e.reason as { message?: string; stack?: string } | string | undefined;
    const message = typeof r === "string" ? r : r?.message ?? "unhandled rejection";
    const stack = typeof r === "object" && r?.stack ? r.stack : "";
    report(base, "unhandledrejection", message, stack);
  });
}
